import React, { useState, useEffect } from 'react';
import { 
    Building2, Users, Plus, Settings, Trash2, UserPlus, 
    Crown, Shield, User, X, Mail, Check, CreditCard, 
    AlertTriangle, Calendar, PlusCircle, ArrowRight
} from 'lucide-react';
import { useTranslation } from 'react-i18next';
import API from '../api';
import { useActiveOrg } from '../contexts/ActiveOrgContext';

const formatMinutes = (val) => {
    if (val === undefined || val === null) return '0.00';
    return Number(val).toFixed(2);
};

const Customers = () => {
    const { t } = useTranslation();
    const { activeOrg } = useActiveOrg();
    const [customers, setCustomers] = useState([]);
    const [organizations, setOrganizations] = useState([]);
    const [packages, setPackages] = useState([]);
    const [systemUsers, setSystemUsers] = useState([]);
    const [loading, setLoading] = useState(true);
    
    // Modals
    const [showCreateModal, setShowCreateModal] = useState(false);
    const [showDetailModal, setShowDetailModal] = useState(null);
    const [currentUser, setCurrentUser] = useState(null);
    
    // State alerts
    const [error, setError] = useState(null);
    const [success, setSuccess] = useState(null);

    // Form inputs: New Customer
    const [newCustomer, setNewCustomer] = useState({
        company_name: '',
        organization: '',
        notification_emails: []
    });
    const [contactNameInput, setContactNameInput] = useState('');
    const [contactEmailInput, setContactEmailInput] = useState('');
    const [detailContactNameInput, setDetailContactNameInput] = useState('');
    const [detailContactEmailInput, setDetailContactEmailInput] = useState('');

    // Form inputs: Member additions
    const [newMember, setNewMember] = useState({
        user_id: '',
        role: 'viewer'
    });

    useEffect(() => {
        fetchData();
    }, [activeOrg]);

    useEffect(() => {
        setNewCustomer(prev => ({
            ...prev,
            organization: activeOrg ? activeOrg.id : ''
        }));
    }, [activeOrg]);

    const fetchData = async () => {
        setLoading(true);
        try {
            const orgParam = activeOrg ? `organization=${activeOrg.id}&` : '';
            const [custRes, orgRes, pkgRes, userRes, meRes] = await Promise.all([
                API.get(`/customers/?${orgParam}_t=${Date.now()}`),
                API.get(`/organizations/?_t=${Date.now()}`),
                API.get(`/minute-packages/?_t=${Date.now()}`),
                API.get(`/users/?_t=${Date.now()}`),
                API.get(`/me/?_t=${Date.now()}`).catch(() => ({ data: { is_superuser: false, is_staff: false } }))
            ]);
            setCustomers(custRes.data);
            setOrganizations(orgRes.data);
            setPackages(pkgRes.data);
            setSystemUsers(userRes.data);
            setCurrentUser(meRes.data);
            setLoading(false);
        } catch (err) {
            console.error("Customers page fetch error:", err);
            setError(t('customers.error_fetch_data', 'Veriler yüklenirken hata oluştu.'));
            setLoading(false);
        }
    };

    const handleCreateCustomer = async () => {
        if (!newCustomer.company_name) {
            setError(t('customers.error_name_required', 'Müşteri adı gereklidir.'));
            return;
        }

        const payload = {
            company_name: newCustomer.company_name,
            organization: newCustomer.organization || null,
            notification_emails: newCustomer.notification_emails
        };

        try {
            await API.post('/customers/', payload);
            setSuccess(t('customers.success_created', 'Müşteri başarıyla oluşturuldu.'));
            setShowCreateModal(false);
            setNewCustomer({
                company_name: '',
                organization: activeOrg ? activeOrg.id : '',
                notification_emails: []
            });
            fetchData();
        } catch (err) {
            console.error(err);
            setError(err.response?.data?.detail || t('customers.error_create_failed', 'Müşteri oluşturulurken hata oluştu.'));
        }
    };

    const handleDeleteCustomer = async (id) => {
        if (window.confirm(t('customers.confirm_delete', 'Bu müşteriyi silmek istediğinizden emin misiniz? Bu işlem geri alınamaz!'))) {
            try {
                await API.delete(`/customers/${id}/`);
                setSuccess(t('customers.success_deleted', 'Müşteri silindi.'));
                fetchData();
                if (showDetailModal?.id === id) {
                    setShowDetailModal(null);
                }
            } catch (err) {
                setError(t('customers.error_delete_failed', 'Müşteri silinirken hata oluştu.'));
            }
        }
    };

    const handleAddContact = () => {
        const nameVal = contactNameInput.trim();
        const emailVal = contactEmailInput.trim();
        if (!nameVal || !emailVal) return;

        if (newCustomer.notification_emails.some(c => c.email === emailVal)) {
            alert(t('customers.error_email_exists', 'Bu e-posta adresi zaten eklenmiş.'));
            return;
        }

        setNewCustomer({
            ...newCustomer,
            notification_emails: [...newCustomer.notification_emails, { name: nameVal, email: emailVal }]
        });
        setContactNameInput('');
        setContactEmailInput('');
    };

    const handleRemoveContact = (emailToRemove) => {
        setNewCustomer({
            ...newCustomer,
            notification_emails: newCustomer.notification_emails.filter(c => c.email !== emailToRemove)
        });
    };

    // Customer Detail Multi-Email management
    const handleDetailAddContact = async (customerId, currentEmails) => {
        const nameVal = detailContactNameInput.trim();
        const emailVal = detailContactEmailInput.trim();
        if (!nameVal || !emailVal) return;

        const normalizedEmails = (currentEmails || []).map(item => 
            typeof item === 'string' ? { name: '', email: item } : item
        );

        if (normalizedEmails.some(c => c.email === emailVal)) {
            alert(t('customers.error_email_exists', 'Bu e-posta adresi zaten eklenmiş.'));
            return;
        }

        const updatedEmails = [...normalizedEmails, { name: nameVal, email: emailVal }];
        try {
            const res = await API.patch(`/customers/${customerId}/`, { notification_emails: updatedEmails });
            setShowDetailModal({
                ...showDetailModal,
                notification_emails: res.data.notification_emails
            });
            setDetailContactNameInput('');
            setDetailContactEmailInput('');
            setSuccess(t('customers.success_email_added', 'E-posta eklendi.'));
            fetchData();
        } catch (err) {
            setError(t('customers.error_email_add_failed', 'E-posta eklenirken hata oluştu.'));
        }
    };

    const handleDetailRemoveContact = async (customerId, currentEmails, emailToRemove) => {
        const normalizedEmails = (currentEmails || []).map(item => 
            typeof item === 'string' ? { name: '', email: item } : item
        );
        const updatedEmails = normalizedEmails.filter(c => c.email !== emailToRemove);
        try {
            const res = await API.patch(`/customers/${customerId}/`, { notification_emails: updatedEmails });
            setShowDetailModal({
                ...showDetailModal,
                notification_emails: res.data.notification_emails
            });
            setSuccess(t('customers.success_email_removed', 'E-posta kaldırıldı.'));
            fetchData();
        } catch (err) {
            setError(t('customers.error_email_remove_failed', 'E-posta kaldırılırken hata oluştu.'));
        }
    };

    // User management (add/remove from customer)
    const handleAddMember = async (customerId) => {
        if (!newMember.user_id) {
            setError(t('customers.error_user_required', 'Kullanıcı seçimi gereklidir.'));
            return;
        }

        try {
            const res = await API.post(`/customers/${customerId}/add-member/`, {
                user_id: parseInt(newMember.user_id),
                role: newMember.role
            });
            setSuccess(t('customers.success_member_added', 'Kullanıcı müşteriye atandı.'));
            
            // Detay modal state'ini güncelle
            const updatedMembers = [...(showDetailModal.members || [])];
            const index = updatedMembers.findIndex(m => m.user === res.data.user);
            if (index !== -1) {
                updatedMembers[index] = res.data;
            } else {
                updatedMembers.push(res.data);
            }

            setShowDetailModal({
                ...showDetailModal,
                members: updatedMembers
            });

            setNewMember({ user_id: '', role: 'viewer' });
            fetchData();
        } catch (err) {
            setError(err.response?.data?.error || t('customers.error_member_add_failed', 'Kullanıcı atanırken hata oluştu.'));
        }
    };

    const handleRemoveMember = async (customerId, userId) => {
        if (window.confirm(t('customers.confirm_remove_member', 'Bu kullanıcının müşteri üyeliğini kaldırmak istediğinize emin misiniz?'))) {
            try {
                await API.post(`/customers/${customerId}/remove-member/`, {
                    user_id: userId
                });
                setSuccess(t('customers.success_member_removed', 'Kullanıcı üyeliği kaldırıldı.'));
                
                // Detay modal state'ini güncelle
                setShowDetailModal({
                    ...showDetailModal,
                    members: (showDetailModal.members || []).filter(m => m.user !== userId)
                });
                fetchData();
            } catch (err) {
                setError(t('customers.error_member_remove_failed', 'Kullanıcı çıkarılırken hata oluştu.'));
            }
        }
    };

    const handleMemberPrefToggle = async (member, field) => {
        try {
            const newValue = !member[field];
            const res = await API.patch(`/customer-members/${member.id}/`, {
                [field]: newValue
            });
            
            const updatedMembers = showDetailModal.members.map(m => {
                if (m.id === member.id) {
                    return res.data;
                }
                return m;
            });
            
            setShowDetailModal({
                ...showDetailModal,
                members: updatedMembers
            });
            setSuccess(t('customers.success_member_updated', 'Üye bildirim tercihleri güncellendi.'));
            setTimeout(() => setSuccess(null), 3000);
        } catch (err) {
            setError(t('customers.error_member_update_failed', 'Üye güncellenemedi.'));
            setTimeout(() => setError(null), 3000);
        }
    };

    // Balance Addition logic removed - now handled in Django Admin

    const getRoleBadgeColor = (role) => {
        switch (role) {
            case 'admin': return 'bg-orange-100 text-orange-800 border-orange-200';
            case 'operator': return 'bg-blue-100 text-blue-800 border-blue-200';
            default: return 'bg-slate-100 text-slate-800 border-slate-200';
        }
    };

    if (loading) return (
        <div className="flex items-center justify-center p-12 min-h-[50vh]">
            <div className="animate-spin rounded-full h-10 w-10 border-t-2 border-b-2 border-blue-600"></div>
        </div>
    );

    return (
        <div className="space-y-6">
            {/* Header */}
            <div className="flex items-center justify-between">
                <div>
                    <h1 className="text-2xl font-bold text-gray-900">{t('customers.title', 'Dakika Paket Bilgisi')}</h1>
                    <p className="text-gray-500 mt-1">{t('customers.subtitle', 'Müşteri firmaları, bildirim kanalları ve bakiye tanımları')}</p>
                </div>
                {(currentUser?.is_superuser || currentUser?.is_staff) && (
                    <button
                        onClick={() => setShowCreateModal(true)}
                        className="flex items-center px-4 py-2 bg-blue-600 text-white rounded-lg hover:bg-blue-700 transition-colors shadow-sm"
                    >
                        <Plus className="w-5 h-5 mr-2" />
                        {t('customers.new_customer', 'Yeni Müşteri Ekle')}
                    </button>
                )}
            </div>

            {/* Notifications */}
            {error && (
                <div className="p-4 bg-red-50 border border-red-200 rounded-lg flex justify-between items-center text-red-700">
                    <span className="flex items-center gap-2">
                        <AlertTriangle size={18} />
                        {error}
                    </span>
                    <button onClick={() => setError(null)}><X size={18} /></button>
                </div>
            )}

            {success && (
                <div className="p-4 bg-green-50 border border-green-200 rounded-lg flex justify-between items-center text-green-700">
                    <span className="flex items-center gap-2">
                        <Check size={18} />
                        {success}
                    </span>
                    <button onClick={() => setSuccess(null)}><X size={18} /></button>
                </div>
            )}

            {/* Grid of Customer Cards */}
            <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6">
                {customers.length === 0 ? (
                    <div className="col-span-full bg-white rounded-xl shadow-sm p-8 border border-gray-150 text-center">
                        <Building2 className="w-12 h-12 text-gray-300 mx-auto mb-4" />
                        <p className="text-gray-500 font-medium">{t('customers.no_customers', 'Henüz müşteri tanımlanmamış')}</p>
                    </div>
                ) : (
                    customers.map((cust) => {
                        const balance = cust.active_balance;
                        const hasActiveBalance = balance && balance.is_active && (balance.minutes_remaining > 0 || balance.is_unlimited);
                        return (
                            <div key={cust.id} className="bg-white rounded-xl shadow-sm border border-gray-200 p-6 flex flex-col justify-between hover:shadow-md hover:border-blue-100 transition-all">
                                <div>
                                    <div className="flex items-start justify-between mb-4">
                                        <div className="p-3 bg-blue-50 text-blue-600 rounded-lg">
                                            <Building2 size={24} />
                                        </div>
                                        <div className="flex gap-2">
                                            {(currentUser?.is_superuser || currentUser?.is_staff) && (
                                                <button
                                                    onClick={() => setShowDetailModal(cust)}
                                                    className="p-1.5 text-gray-500 hover:text-blue-600 hover:bg-slate-50 rounded"
                                                    title={t('common.edit', 'Düzenle')}
                                                >
                                                    <Settings size={18} />
                                                </button>
                                            )}
                                            {(currentUser?.is_superuser || currentUser?.is_staff) && (
                                                <button
                                                    onClick={() => handleDeleteCustomer(cust.id)}
                                                    className="p-1.5 text-gray-500 hover:text-red-600 hover:bg-slate-50 rounded"
                                                    title={t('common.delete', 'Sil')}
                                                >
                                                    <Trash2 size={18} />
                                                </button>
                                            )}
                                        </div>
                                    </div>

                                    <h3 className="text-lg font-bold text-gray-900">{cust.company_name}</h3>
                                    
                                    {/* Org Connection */}
                                    <div className="flex items-center text-sm text-gray-500 mt-2 gap-1.5">
                                        <Building2 size={14} className="text-gray-400" />
                                        <span>Org: {organizations.find(o => o.id === cust.organization)?.name || t('customers.no_connection', 'Bağlantı Yok')}</span>
                                    </div>

                                    {/* Balance Indicator */}
                                    <div className="mt-4 p-3 bg-gray-50 rounded-lg border border-gray-100 space-y-2">
                                        <div className="flex items-center justify-between">
                                            <div className="flex items-center gap-2 text-sm text-gray-700">
                                                <CreditCard size={16} className="text-blue-500" />
                                                <span className="font-semibold">{t('customers.minute_balance', 'Dakika Bakiyesi')}</span>
                                            </div>
                                            <div>
                                                {balance && balance.is_active ? (
                                                    <span className="bg-green-100 text-green-800 text-xs font-bold px-2.5 py-1 rounded-full border border-green-200">
                                                        {t('customers.status_active', 'Aktif')}
                                                    </span>
                                                ) : (
                                                    <span className="bg-red-100 text-red-800 text-xs font-bold px-2.5 py-1 rounded-full border border-red-200">
                                                        {t('customers.status_passive', 'Pasif')}
                                                    </span>
                                                )}
                                            </div>
                                        </div>
                                        {balance ? (
                                            <div className="text-xs text-gray-600 space-y-1.5">
                                                <div className="flex justify-between">
                                                    <span>{t('customers.total_limit_label', 'Toplam Limit:')}</span>
                                                    <span className="font-semibold text-gray-700 font-mono">
                                                        {balance.is_unlimited ? t('customers.unlimited', 'Sınırsız') : `${formatMinutes(balance.minutes_balance)} dk`}
                                                    </span>
                                                </div>
                                                <div className="flex justify-between">
                                                    <span>{t('customers.used_label', 'Kullanılan:')}</span>
                                                    <span className="font-semibold text-gray-700 font-mono">{formatMinutes(balance.minutes_used)} dk</span>
                                                </div>
                                                <div className="flex justify-between border-t border-gray-200 pt-1.5 mt-1">
                                                    <span className="font-semibold text-blue-800">{t('customers.remaining_balance_label', 'Kalan Bakiye:')}</span>
                                                    <span className="font-bold text-blue-900 font-mono">
                                                        {balance.is_unlimited ? t('customers.unlimited', 'Sınırsız') : `${formatMinutes(balance.minutes_remaining !== undefined ? balance.minutes_remaining : balance.minutes_balance)} dk`}
                                                    </span>
                                                </div>
                                            </div>
                                        ) : (
                                            <p className="text-xs text-gray-400 italic">{t('customers.no_balance_defined', 'Tanımlı bakiye yok.')}</p>
                                        )}
                                    </div>

                                    {/* Email lists */}
                                    <div className="mt-4 space-y-1.5">
                                        <p className="text-xs font-semibold text-gray-500 uppercase tracking-wider flex items-center gap-1">
                                            <Mail size={12} /> {t('customers.emails_title', 'Bildirim Mailleri')}
                                        </p>
                                        <div className="flex flex-col gap-1">
                                            {cust.notification_emails && cust.notification_emails.length > 0 ? (
                                                cust.notification_emails.map((email, idx) => {
                                                    const displayVal = typeof email === 'string' ? email : `${email.name || ''} (${email.email || ''})`;
                                                    return (
                                                        <span key={idx} className="bg-slate-50 text-slate-700 text-xs px-2 py-1 rounded border border-slate-200 w-full flex items-center gap-1.5" title={displayVal}>
                                                                <Mail size={10} className="text-slate-400 flex-shrink-0" />
                                                                <span className="break-all">{displayVal}</span>
                                                        </span>
                                                    );
                                                })
                                            ) : (
                                                <span className="text-xs text-gray-400 italic">{t('customers.no_emails_defined', 'E-posta tanımlanmamış.')}</span>
                                            )}
                                        </div>
                                    </div>
                                </div>

                                {/* Members listing preview */}
                                <div className="mt-6 pt-4 border-t border-gray-100 flex items-center justify-between">
                                    <div className="flex items-center">
                                        <Users size={14} className="text-gray-400 mr-1.5" />
                                        <span className="text-xs text-gray-600 font-medium">
                                            {t('customers.members_count', '{{count}} kullanıcı üye', { count: cust.members ? cust.members.length : 0 })}
                                        </span>
                                    </div>
                                    <div className="flex -space-x-2 overflow-hidden">
                                        {cust.members && cust.members.slice(0, 3).map((member, mIdx) => (
                                            <div
                                                key={member.id}
                                                className="inline-block h-6 w-6 rounded-full ring-2 ring-white bg-blue-600 text-white flex items-center justify-center text-[10px] font-bold"
                                                title={`${member.username} (${member.role_display})`}
                                            >
                                                {member.username.charAt(0).toUpperCase()}
                                            </div>
                                        ))}
                                        {cust.members && cust.members.length > 3 && (
                                            <div className="inline-block h-6 w-6 rounded-full ring-2 ring-white bg-slate-200 text-slate-600 flex items-center justify-center text-[10px] font-bold">
                                                +{cust.members.length - 3}
                                            </div>
                                        )}
                                    </div>
                                </div>
                            </div>
                        );
                    })
                )}
            </div>

            {/* Create Customer Modal */}
            {showCreateModal && (
                <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-[60] p-4">
                    <div className="bg-white rounded-xl shadow-xl p-6 w-full max-w-lg max-h-[90vh] overflow-y-auto border border-slate-200">
                        <div className="flex justify-between items-center mb-6">
                            <h2 className="text-xl font-bold text-gray-900">{t('customers.new_customer', 'Yeni Müşteri Ekle')}</h2>
                            <button onClick={() => setShowCreateModal(false)} className="text-gray-400 hover:text-gray-600">
                                <X size={24} />
                            </button>
                        </div>

                        <div className="space-y-4">
                            <div>
                                <label className="block text-sm font-medium text-gray-700 mb-1">{t('customers.company_name_required', 'Müşteri Firma Adı *')}</label>
                                <input
                                    type="text"
                                    value={newCustomer.company_name}
                                    onChange={(e) => setNewCustomer({ ...newCustomer, company_name: e.target.value })}
                                    className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-transparent"
                                    placeholder="Örn: Elor Holding"
                                />
                            </div>

                            <div>
                                <label className="block text-sm font-medium text-gray-700 mb-1">{t('customers.linked_org', 'Bağlı Organizasyon')}</label>
                                <select
                                    value={newCustomer.organization}
                                    onChange={(e) => setNewCustomer({ ...newCustomer, organization: e.target.value })}
                                    className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500"
                                >
                                    <option value="">{t('customers.select_org_placeholder', '-- Organizasyon Seçin --')}</option>
                                    {organizations.map(org => (
                                        <option key={org.id} value={org.id}>{org.name}</option>
                                    ))}
                                </select>
                            </div>

                            {/* Multi Email Setup */}
                            <div className="space-y-2">
                                <label className="block text-sm font-medium text-gray-700">{t('customers.notification_emails', 'Bildirim E-postaları')}</label>
                                <div className="flex gap-2">
                                    <input
                                        type="text"
                                        value={contactNameInput}
                                        onChange={(e) => setContactNameInput(e.target.value)}
                                        className="flex-1 px-3 py-1.5 border border-gray-300 rounded-lg text-sm"
                                        placeholder={t('customers.name_lastname', 'Ad Soyad')}
                                    />
                                    <input
                                        type="email"
                                        value={contactEmailInput}
                                        onChange={(e) => setContactEmailInput(e.target.value)}
                                        className="flex-1 px-3 py-1.5 border border-gray-300 rounded-lg text-sm"
                                        placeholder={t('customers.email', 'E-posta')}
                                    />
                                    <button
                                        type="button"
                                        onClick={handleAddContact}
                                        className="px-4 py-1.5 bg-blue-600 text-white text-sm font-semibold rounded-lg hover:bg-blue-700 transition-colors"
                                    >
                                        {t('common.add', 'Ekle')}
                                    </button>
                                </div>
                                
                                <div className="flex flex-wrap gap-1.5 mt-2">
                                    {newCustomer.notification_emails.map((contact, idx) => {
                                        const displayVal = typeof contact === 'string' ? contact : `${contact.name || ''} (${contact.email})`;
                                        const emailVal = typeof contact === 'string' ? contact : contact.email;
                                        return (
                                            <span key={idx} className="inline-flex items-center bg-blue-50 text-blue-700 text-xs px-2.5 py-1 rounded border border-blue-100 font-medium">
                                                {displayVal}
                                                <button 
                                                    type="button"
                                                    onClick={() => handleRemoveContact(emailVal)}
                                                    className="ml-1.5 text-blue-500 hover:text-blue-800"
                                                >
                                                    <X size={12} />
                                                </button>
                                            </span>
                                        );
                                    })}
                                </div>
                            </div>
                        </div>

                        <div className="flex justify-end gap-3 mt-6">
                            <button
                                onClick={() => setShowCreateModal(false)}
                                className="px-4 py-2 text-gray-700 hover:bg-gray-100 rounded-lg transition-colors"
                            >
                                {t('common.cancel', 'İptal')}
                            </button>
                            <button
                                onClick={handleCreateCustomer}
                                className="px-4 py-2 bg-blue-600 text-white rounded-lg hover:bg-blue-700 transition-colors shadow-sm"
                            >
                                {t('customers.create_customer_btn', 'Müşteri Oluştur')}
                            </button>
                        </div>
                    </div>
                </div>
            )}

            {/* Detail Modal */}
            {showDetailModal && (
                <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-[60] p-4">
                    <div className="bg-white rounded-xl shadow-xl p-6 w-full max-w-2xl max-h-[90vh] overflow-y-auto border border-slate-200">
                        <div className="flex justify-between items-center mb-6">
                            <h2 className="text-xl font-bold text-gray-900">{t('customers.management_title', '{{company}} Yönetimi', { company: showDetailModal.company_name })}</h2>
                            <button onClick={() => setShowDetailModal(null)} className="text-gray-400 hover:text-gray-600">
                                <X size={24} />
                            </button>
                        </div>

                        <div className="space-y-6">
                            {/* Emails tags */}
                            <div className="bg-slate-50 p-4 rounded-lg border border-slate-200">
                                <h3 className="font-bold text-gray-800 mb-3 flex items-center gap-1.5">
                                    <Mail size={16} className="text-blue-600" />
                                    {t('customers.email_management', 'Bildirim E-postaları Yönetimi')}
                                </h3>
                                
                                <div className="flex flex-wrap gap-1.5 mb-4">
                                    {showDetailModal.notification_emails && showDetailModal.notification_emails.map((contact, idx) => {
                                        const displayVal = typeof contact === 'string' ? contact : `${contact.name || ''} (${contact.email})`;
                                        const emailVal = typeof contact === 'string' ? contact : contact.email;
                                        return (
                                            <span key={idx} className="inline-flex items-center bg-blue-50 text-blue-700 text-xs px-2.5 py-1 rounded border border-blue-100 font-medium">
                                                {displayVal}
                                                <button 
                                                    onClick={() => handleDetailRemoveContact(showDetailModal.id, showDetailModal.notification_emails, emailVal)}
                                                    className="ml-1.5 text-blue-500 hover:text-blue-800"
                                                >
                                                    <X size={12} />
                                                </button>
                                            </span>
                                        );
                                    })}
                                    {(!showDetailModal.notification_emails || showDetailModal.notification_emails.length === 0) && (
                                        <span className="text-sm text-gray-400 italic">{t('customers.no_emails_registered', 'Kayıtlı e-posta yok.')}</span>
                                    )}
                                </div>

                                <div className="flex gap-2">
                                    <input
                                        type="text"
                                        value={detailContactNameInput}
                                        onChange={(e) => setDetailContactNameInput(e.target.value)}
                                        className="flex-1 px-3 py-1.5 border border-gray-300 rounded-lg text-sm"
                                        placeholder={t('customers.name_lastname', 'Ad Soyad')}
                                    />
                                    <input
                                        type="email"
                                        value={detailContactEmailInput}
                                        onChange={(e) => setDetailContactEmailInput(e.target.value)}
                                        className="flex-1 px-3 py-1.5 border border-gray-300 rounded-lg text-sm"
                                        placeholder={t('customers.email', 'E-posta')}
                                    />
                                    <button
                                        onClick={() => handleDetailAddContact(showDetailModal.id, showDetailModal.notification_emails || [])}
                                        className="px-4 py-1.5 bg-blue-600 text-white text-sm font-semibold rounded-lg hover:bg-blue-700 transition-colors"
                                    >
                                        {t('common.add', 'Ekle')}
                                    </button>
                                </div>
                            </div>

                            {/* Members / Permissions Panel */}
                            {currentUser?.is_superuser && (
                                <div className="bg-slate-50 p-4 rounded-lg border border-slate-200">
                                    <h3 className="font-bold text-gray-800 mb-3 flex items-center gap-1.5">
                                        <Users size={16} className="text-blue-600" />
                                        {t('customers.manage_users', 'Üye Kullanıcılar ve Yetki Yönetimi')}
                                    </h3>

                                    <div className="space-y-2 mb-4">
                                        {showDetailModal.members && showDetailModal.members.length > 0 ? (
                                            showDetailModal.members.map((member) => (
                                                <div key={member.id} className="flex items-center justify-between bg-white p-3 rounded-lg border border-slate-100">
                                                    <div className="flex items-center">
                                                        <div className="w-8 h-8 bg-blue-100 text-blue-600 rounded-full flex items-center justify-center mr-3 font-semibold text-xs">
                                                            {member.username.charAt(0).toUpperCase()}
                                                        </div>
                                                        <div>
                                                            <p className="text-sm font-semibold text-gray-900">{member.username}</p>
                                                            <p className="text-xs text-gray-500">{member.email}</p>
                                                            <div className="mt-2 flex gap-4 text-xs text-gray-500">
                                                                <label className="flex items-center space-x-1 cursor-pointer">
                                                                    <input
                                                                        type="checkbox"
                                                                        checked={member.notify_on_start || false}
                                                                        onChange={() => handleMemberPrefToggle(member, 'notify_on_start')}
                                                                        className="rounded text-blue-600 focus:ring-blue-500"
                                                                    />
                                                                    <span>{t('settings.notify_on_start', 'Başladı')}</span>
                                                                </label>
                                                                <label className="flex items-center space-x-1 cursor-pointer">
                                                                    <input
                                                                        type="checkbox"
                                                                        checked={member.notify_on_success || false}
                                                                        onChange={() => handleMemberPrefToggle(member, 'notify_on_success')}
                                                                        className="rounded text-blue-600 focus:ring-blue-500"
                                                                    />
                                                                    <span>{t('settings.notify_on_success', 'Başarılı')}</span>
                                                                </label>
                                                                <label className="flex items-center space-x-1 cursor-pointer">
                                                                    <input
                                                                        type="checkbox"
                                                                        checked={member.notify_on_failure || false}
                                                                        onChange={() => handleMemberPrefToggle(member, 'notify_on_failure')}
                                                                        className="rounded text-blue-600 focus:ring-blue-500"
                                                                    />
                                                                    <span>{t('settings.notify_on_failure', 'Hatalı')}</span>
                                                                </label>
                                                            </div>
                                                        </div>
                                                    </div>
                                                    <div className="flex items-center gap-3">
                                                        <span className={`text-xs px-2 py-0.5 rounded border font-semibold ${getRoleBadgeColor(member.role)}`}>
                                                            {member.role === 'admin' ? t('customers.role_admin', 'Yönetici') : member.role === 'operator' ? t('customers.role_operator', 'Operatör') : t('customers.role_viewer', 'İzleyici')}
                                                        </span>
                                                        <button 
                                                            onClick={() => handleRemoveMember(showDetailModal.id, member.user)}
                                                            className="text-red-500 hover:text-red-800 p-1 hover:bg-red-50 rounded"
                                                            title={t('customers.remove_membership', 'Üyeliği Kaldır')}
                                                        >
                                                            <X size={16} />
                                                        </button>
                                                    </div>
                                                </div>
                                            ))
                                        ) : (
                                            <p className="text-sm text-gray-400 italic">{t('customers.no_members_assigned', 'Müşteriye atanmış herhangi bir kullanıcı bulunmuyor.')}</p>
                                        )}
                                    </div>

                                    {/* Invite User Form */}
                                    <div className="border-t pt-4 grid grid-cols-1 md:grid-cols-3 gap-3">
                                        <div className="md:col-span-2">
                                            <label className="block text-xs font-semibold text-gray-500 uppercase tracking-wider mb-1">{t('customers.select_user', 'Kullanıcı Seçin')}</label>
                                            <select
                                                value={newMember.user_id}
                                                onChange={(e) => setNewMember({ ...newMember, user_id: e.target.value })}
                                                className="w-full px-3 py-1.5 border border-gray-300 rounded-lg text-sm bg-white"
                                            >
                                                <option value="">{t('customers.select_user_placeholder', '-- Kullanıcı Seçin --')}</option>
                                                {systemUsers.map(user => (
                                                    <option key={user.id} value={user.id}>{user.username} ({user.email})</option>
                                                ))}
                                            </select>
                                        </div>
                                        <div>
                                            <label className="block text-xs font-semibold text-gray-500 uppercase tracking-wider mb-1">{t('customers.role_label', 'Yetki Rolü')}</label>
                                            <div className="flex gap-2">
                                                <select
                                                    value={newMember.role}
                                                    onChange={(e) => setNewMember({ ...newMember, role: e.target.value })}
                                                    className="flex-1 px-3 py-1.5 border border-gray-300 rounded-lg text-sm bg-white"
                                                >
                                                    <option value="admin">{t('customers.role_admin', 'Yönetici')}</option>
                                                    <option value="operator">{t('customers.role_operator', 'Operatör')}</option>
                                                    <option value="viewer">{t('customers.role_viewer', 'İzleyici')}</option>
                                                </select>
                                                <button
                                                    onClick={() => handleAddMember(showDetailModal.id)}
                                                    className="px-3 bg-blue-600 text-white rounded-lg hover:bg-blue-700 transition-colors"
                                                    title={t('customers.add_member', 'Üyeyi Ekle')}
                                                >
                                                    <UserPlus size={16} />
                                                </button>
                                            </div>
                                        </div>
                                    </div>
                                </div>
                            )}
                        </div>
                    </div>
                </div>
            )}


        </div>
    );
};

export default Customers;
